import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

type StatusType = 'accept' | 'close' | 'new';

const statusConfig: Record<
  StatusType,
  {
    status: string;
    url: string;
    label: string;
    buttonColor: string;
    successMessage: string;
    confirmMessage: string;
  }
> = {
  accept: {
    status: 'ACTIVE',
    url: 'activate',
    label: 'activate',
    buttonColor: 'bg-green-600 hover:bg-green-700',
    successMessage: 'contractActivatedSuccessfully',
    confirmMessage: 'activateContractConfirm',
  },
  close: {
    status: 'CLOSED',
    url: 'close',
    label: 'close',
    buttonColor: 'bg-yellow-500 hover:bg-yellow-600',
    successMessage: 'contractClosedSuccessfully',
    confirmMessage: 'closeContractConfirm',
  },
  new: {
    status: 'NEW',
    url: 'renew',
    label: 'renew',
    buttonColor: 'bg-blue-500 hover:bg-blue-600',
    successMessage: 'contractRenewedSuccessfully',
    confirmMessage: 'renewContractConfirm',
  },
};

interface ChangeStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: StatusType;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function ChangeStatusDialog({
  open,
  onOpenChange,
  type,
  onConfirm,
  isLoading,
}: ChangeStatusDialogProps) {
  const { t } = useTranslation();
  const config = statusConfig[type];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(config.confirmMessage)}
          </AlertDialogDescription>
          {type === 'new' && (
            <p className="text-sm font-medium text-destructive">
              {t('renewContractDeletesTasksWarning')}
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className={`${config.buttonColor} text-white`}
          >
            {isLoading ? t('processing') : t(config.label)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Trigger button variant
interface ChangeStatusButtonProps {
  type: StatusType;
  onClick: () => void;
  isLoading?: boolean;
}

export function ChangeStatusButton({ type, onClick, isLoading }: ChangeStatusButtonProps) {
  const { t } = useTranslation();
  const config = statusConfig[type];

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={isLoading}
      className={`${config.buttonColor} text-white`}
    >
      {t(config.label)}
    </Button>
  );
}
